<script setup lang="ts">
import { computed, ref } from "vue";
import {
  FormDefinition,
  FormElementRule,
  RuleConditionBase,
  RuleConditionType,
  WorkflowDefinition,
} from "~/api";
import {
  conditionDisplayNames,
  loginAccountConditionDisplayNames,
  statusConditionDisplayNames,
  timeFrameConditionDisplayNames,
} from "~/utils/enumDisplayNames";
import {
  isFormElementBasedCondition,
  isFormStatusBasedCondition,
  isLoginAccountBasedCondition,
  isTimeFrameBasedCondition,
  isWorkflowStatusBasedCondition,
} from "~/utils/typeGuards";

import { Delete, Edit } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { Slotted } from "~/types/Slotted";
import FormRuleEditor, { EditCondition } from "./FormRuleEditor.vue";
import FormStatusBasedRuleEditor from "./FormStatusBasedRuleEditor.vue";
import LoginAccountBasedRuleEditor from "./LoginAccountBasedRuleEditor.vue";
import WorkflowBasedRuleEditor from "./WorkflowBasedRuleEditor.vue";

import { useOrgInfoStore } from "~/stores/OrgInfoStore";
import { showError } from "~/utils/handleAPIError";
import TimeFrameBasedRuleEditor from "./TimeFrameBasedRuleEditor.vue";

const emit = defineEmits<{
  (e: "update:modelValue", value: FormDefinition): void;
  (e: "closeRuleDrawer", value: boolean): void;
}>();

const props = defineProps<{
  workflowDefinition?: WorkflowDefinition;
}>();

const model = defineModel<FormDefinition>({ required: true });

/** Editing a Condition */
/** the condition object that was clicked to edit */
const editingCondition = ref<EditCondition | undefined>();
const editingWorkflowCondition = ref<EditCondition | undefined>();
const editingFormStatusCondition = ref<EditCondition | undefined>();
const editingFormElementCondition = ref<EditCondition | undefined>();
const editingLoginAccountCondition = ref<EditCondition | undefined>();
const editingTimeFrameCondition = ref<EditCondition | undefined>();

/** the current search query */
const rulesSearchQuery = ref<string>();
const filteredRules = computed<FormElementRule[]>(() => {
  let result = model.value.rules;
  if (editingCondition.value !== undefined) {
    result = result.map((r) => ({
      ...r,
      conditions: r.conditions
        .filter((c) => c !== editingCondition.value?.condition)
        .sort(({ guid: guidA }, { guid: guidB }) => {
          if (guidA < guidB) {
            return -1;
          }
          if (guidA > guidB) {
            return 1;
          }
          return 0;
        }),
    }));
  }

  if (!rulesSearchQuery.value) {
    return result;
  }

  const queryLower = rulesSearchQuery.value?.toLowerCase();
  result = result.filter((r) =>
    JSON.stringify(r).toLowerCase().includes(queryLower),
  );
  return result;
});

/** the status list for display purposes */
const statusOptions = ref<Record<string, string>>(
  Object.fromEntries(
    (
      props.workflowDefinition?.statusList ?? [
        { label: "Started", code: "Started" },
        { label: "In Progress", code: "In Progress" },
        { label: "Completed", code: "Completed" },
      ]
    )
      .filter((s) => s.label !== null && s.code !== null)
      .map((s) => [s.code!, s.label!]),
  ),
);

const orgInfoStore = useOrgInfoStore();

const getOrgGroupName = (orgGroupGuid: string) =>
  orgInfoStore.MyOrgInfo?.orgGroupDtos.find((og) => og.guid === orgGroupGuid)
    ?.name || "";

/**
 * update the condition with the new answer value
 */
const handleEditCondition = (
  rule: FormElementRule,
  condition: RuleConditionBase,
) => {
  editingCondition.value = {
    condition,
    rule,
  };

  if (condition.conditionType === RuleConditionType.WORKFLOW_STATUS_BASED) {
    editingWorkflowCondition.value = editingCondition.value;
  }
  if (condition.conditionType === RuleConditionType.FORM_ELEMENT_BASED) {
    editingFormElementCondition.value = editingCondition.value;
  }
  if (condition.conditionType === RuleConditionType.FORM_STATUS_BASED) {
    editingFormStatusCondition.value = editingCondition.value;
  }
  if (condition.conditionType === RuleConditionType.LOGIN_ACCOUNT_BASED) {
    editingLoginAccountCondition.value = editingCondition.value;
  }
  if (condition.conditionType === RuleConditionType.TIME_FRAME_BASED) {
    editingTimeFrameCondition.value = editingCondition.value;
  }
};

/**
 * this method deletes the given condition from the given rule
 * @param rule the rule to delete the condition from
 * @param conditionToDelete the condition to delete
 */
const handleDeleteCondition = (
  rule: FormElementRule,
  conditionToDelete: RuleConditionBase,
) => {
  if (!rule) {
    showError("Rule doesn't exist");
    return;
  }

  if (rule.conditions.length === 1) {
    handleDeleteRule(rule);
    return;
  }

  rule.conditions = rule.conditions.filter((c) => {
    if (
      isFormElementBasedCondition(c) &&
      isFormElementBasedCondition(conditionToDelete)
    ) {
      return (
        c.questionCode !== conditionToDelete.questionCode ||
        c.type !== conditionToDelete.type ||
        c.answerValue !== conditionToDelete.answerValue ||
        c.guid !== conditionToDelete.guid
      );
    }
    if (
      isWorkflowStatusBasedCondition(c) &&
      isWorkflowStatusBasedCondition(conditionToDelete)
    ) {
      return (
        c.type !== conditionToDelete.type ||
        c.workflowStatus !== conditionToDelete.workflowStatus ||
        c.guid !== conditionToDelete.guid
      );
    }
    if (
      isFormStatusBasedCondition(c) &&
      isFormStatusBasedCondition(conditionToDelete)
    ) {
      return (
        c.type !== conditionToDelete.type ||
        c.formStatus !== conditionToDelete.formStatus ||
        c.guid !== conditionToDelete.guid
      );
    }
    if (
      isLoginAccountBasedCondition(c) &&
      isLoginAccountBasedCondition(conditionToDelete)
    ) {
      return (
        c.type !== conditionToDelete.type ||
        c.orgGroupGuid !== conditionToDelete.orgGroupGuid ||
        c.guid !== conditionToDelete.guid
      );
    }
    if (
      isTimeFrameBasedCondition(c) &&
      isTimeFrameBasedCondition(conditionToDelete)
    ) {
      return (
        c.type !== conditionToDelete.type ||
        c.timeFrameCode !== conditionToDelete.timeFrameCode ||
        c.guid !== conditionToDelete.guid
      );
    }

    return (
      c.conditionType !== conditionToDelete.conditionType ||
      c.guid !== conditionToDelete.guid
    );
  });

  ElMessage.success("Condition deleted successfully");
};

/**
 * this method deletes the given rule from the form
 * @param rule the rule to delete
 */
const handleDeleteRule = (rule: FormElementRule) => {
  model.value.rules = model.value.rules.filter(
    (r) => JSON.stringify(r) !== JSON.stringify(rule),
  );
  ElMessage.success("Rule deleted successfully");
};
const handleUpdateRules = (newRules: FormElementRule[]) => {
  model.value.rules = newRules;
};
const handleEditingCondition = (condition?: EditCondition) => {
  editingCondition.value = condition;

  if (
    condition?.condition.conditionType === RuleConditionType.FORM_ELEMENT_BASED
  ) {
    editingFormElementCondition.value = condition;
  }
  if (
    condition?.condition.conditionType === RuleConditionType.FORM_STATUS_BASED
  ) {
    editingFormStatusCondition.value = condition;
  }
  if (
    condition?.condition.conditionType ===
    RuleConditionType.WORKFLOW_STATUS_BASED
  ) {
    editingWorkflowCondition.value = condition;
  }
  if (
    condition?.condition.conditionType === RuleConditionType.LOGIN_ACCOUNT_BASED
  ) {
    editingLoginAccountCondition.value = condition;
  }
  if (
    condition?.condition.conditionType === RuleConditionType.TIME_FRAME_BASED
  ) {
    editingTimeFrameCondition.value = condition;
  }
};
const getConditionPrefix = (
  rule: FormElementRule,
  conditionIndex: number,
): string => {
  if (conditionIndex === 0) {
    return "If";
  } else if (rule.trueIfAny) {
    return "or";
  } else {
    return "and";
  }
};

const getFormDefinitionName = (formDefinitionGuid: string): string => {
  const formDefinition = props.workflowDefinition?.formDefinitions?.find(
    (fd) => fd.guid === formDefinitionGuid,
  );
  return formDefinition ? formDefinition.name : "Unknown Form";
};
</script>

<template>
  <div class="rule-editor-page">
    <FormRuleEditor
      v-model:editing-condition="editingFormElementCondition"
      :form="model"
      @update:rules="handleUpdateRules"
      @update:editing-condition="
        ($event?: EditCondition) => handleEditingCondition($event)
      "
    />

    <FormStatusBasedRuleEditor
      v-model:editing-condition="editingFormStatusCondition"
      :form="model"
      :workflow-definition-guid="props.workflowDefinition?.guid ?? ''"
      @update:rules="handleUpdateRules"
      @update:editing-condition="
        ($event?: EditCondition) => handleEditingCondition($event)
      "
    />

    <WorkflowBasedRuleEditor
      v-if="props.workflowDefinition"
      v-model:editing-condition="editingWorkflowCondition"
      :form="model"
      :workflow-definition="props.workflowDefinition"
      @update:rules="handleUpdateRules"
      @update:editing-condition="
        ($event?: EditCondition) => handleEditingCondition($event)
      "
    />
    <LoginAccountBasedRuleEditor
      v-if="props.workflowDefinition"
      v-model:editing-condition="editingLoginAccountCondition"
      :form="model"
      :workflow-definition="props.workflowDefinition"
      @update:rules="handleUpdateRules"
      @update:editing-condition="
        ($event?: EditCondition) => handleEditingCondition($event)
      "
    />
    <TimeFrameBasedRuleEditor
      v-if="props.workflowDefinition"
      v-model:editing-condition="editingTimeFrameCondition"
      :form="model"
      :workflow-definition="props.workflowDefinition"
      @update:rules="handleUpdateRules"
      @update:editing-condition="
        ($event?: EditCondition) => handleEditingCondition($event)
      "
    />

    <el-table
      :data="filteredRules"
      :default-sort="{ prop: 'elementQuestionCode', order: 'descending' }"
      border
      class="rule-editor-table"
    >
      <el-table-column label="Condition(s)" align="center" width="500">
        <template #default="{ row }: Slotted<FormElementRule>">
          <div
            v-for="(condition, i) in row.conditions"
            :key="condition.guid"
            class="rule-condition"
          >
            <div class="rule-condition-details">
              <div>
                <strong>{{ getConditionPrefix(row, i) }}</strong
                >&nbsp;
              </div>
              <div v-if="isWorkflowStatusBasedCondition(condition)">
                <span>
                  <strong>
                    Workflow
                    {{ statusConditionDisplayNames[condition.type] }}
                  </strong>
                  &nbsp;
                </span>
                <span>
                  <el-tag>
                    {{
                      condition.workflowStatus
                        ? (statusOptions[condition.workflowStatus] ??
                          condition.workflowStatus)
                        : "No Status Selected"
                    }}
                  </el-tag>
                </span>
              </div>
              <div v-if="isFormStatusBasedCondition(condition)">
                <span>
                  <strong>
                    {{ getFormDefinitionName(condition.formDefinitionGuid) }}
                    {{ statusConditionDisplayNames[condition.type] }}
                  </strong>
                  &nbsp;
                </span>
                <span>
                  <el-tag>{{ condition.formStatus }}</el-tag>
                </span>
              </div>
              <div v-if="isFormElementBasedCondition(condition)">
                <span
                  v-if="condition.questionCode"
                  class="condition-question-code"
                >
                  <el-tooltip
                    :content="condition.questionGuid ?? undefined"
                    placement="top"
                  >
                    <el-tag>{{ condition.questionCode }}</el-tag>
                  </el-tooltip>
                </span>
                <span class="condition-type">
                  &nbsp;
                  <strong>{{ conditionDisplayNames[condition.type] }}</strong
                  >&nbsp;
                </span>
                <span class="condition-answer-value">
                  {{ condition.answerValue }}
                </span>
              </div>
              <div v-if="isLoginAccountBasedCondition(condition)">
                <span>
                  <strong>
                    User
                    {{ loginAccountConditionDisplayNames[condition.type] }}
                  </strong>
                  &nbsp;
                </span>
                <span>
                  <el-tooltip :content="condition.orgGroupGuid" placement="top">
                    <el-tag>
                      {{ getOrgGroupName(condition.orgGroupGuid) }}
                    </el-tag>
                  </el-tooltip>
                </span>
              </div>
              <div v-if="isTimeFrameBasedCondition(condition)">
                <span>
                  <strong>
                    Date
                    {{ timeFrameConditionDisplayNames[condition.type] }}
                  </strong>
                  &nbsp;
                </span>
                <span>
                  <el-tooltip
                    :content="condition.timeFrameCode ?? undefined"
                    placement="top"
                  >
                    <el-tag>
                      {{ condition.timeFrameCode }}
                    </el-tag>
                  </el-tooltip>
                </span>
              </div>
            </div>
            <el-button
              style="margin-left: auto"
              :icon="Edit"
              circle
              @click="() => handleEditCondition(row, condition)"
            />

            <el-button
              v-if="row.conditions.length > 1"
              :icon="Delete"
              type="danger"
              circle
              @click="handleDeleteCondition(row, condition)"
            />
          </div>
        </template>
      </el-table-column>

      <el-table-column width="60" align="center">
        <strong>Set</strong></el-table-column
      >

      <el-table-column
        sortable
        label="Affected Element"
        prop="elementQuestionCode"
        align="center"
        width="350"
      >
        <template #default="{ row }: Slotted<FormElementRule>">
          <el-tooltip :content="row.elementGuid" placement="top">
            <el-tag type="warning">{{ row.elementQuestionCode }}</el-tag>
          </el-tooltip>
        </template>
      </el-table-column>

      <el-table-column width="50" align="center">
        <strong>to</strong>
      </el-table-column>
      <el-table-column
        sortable
        label="Rule Type"
        prop="ruleType"
        width="120"
        align="center"
      />

      <el-table-column prop="value" align="center" width="150">
        <template #header>
          <el-input
            v-model="rulesSearchQuery"
            clearable
            size="small"
            placeholder="Type to search"
          />
        </template>
        <template #default="{ row }: Slotted<FormElementRule>">
          <el-popconfirm
            title="This will permanently delete this rule and all its conditions. Continue?"
            :width="400"
            @confirm="handleDeleteRule(row)"
          >
            <template #reference>
              <el-button
                :disabled="!!editingCondition"
                type="danger"
                :icon="Delete"
                small
                >Delete</el-button
              >
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<style lang="less" scoped>
.rule-editor-page {
  display: flex;
  flex-direction: column;
  .rule-editor-table {
    width: fit-content;
    align-self: center;
    margin-top: 2em;

    .rule-condition {
      display: flex;
      align-items: center;

      .rule-condition-details {
        display: flex;
        align-items: center;
      }
    }
  }
}

:not(:first-child) {
  margin-top: 4px;
}
</style>
